import { poolLabel, venueLabel } from 'utils/swap/rank'
import type { StockPool } from 'utils/stocks/pricing'

/**
 * How the stock pages turn numbers into text. Shared by the board, the company
 * page and the build-time prerender, so a crawler and a visitor read the same
 * words.
 */

const DASH = '—'

export function formatUsd(value: number | null, digits?: number): string {
  if (value === null || !Number.isFinite(value)) return DASH
  const decimals = digits ?? (value >= 1000 ? 2 : value >= 1 ? 2 : 4)
  return `$${value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  })}`
}

/** Compact dollars for depth and volume: $1.7M, $952. */
export function formatUsdCompact(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return DASH
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}k`
  return `$${value.toFixed(0)}`
}

/** Basis points as a signed percentage, with the sign always shown. */
export function formatBps(bps: number | null): string {
  if (bps === null) return DASH
  const percent = bps / 100
  return `${percent >= 0 ? '+' : ''}${percent.toFixed(2)}%`
}

export function formatPercent(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return DASH
  return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`
}

/** "Aerodrome Slipstream · f3 ts 10" — venue and the pool's own variant. */
export function describePool(pool: StockPool | null): string {
  if (!pool) return DASH
  const label = poolLabel(pool.venue, pool)
  return label ? `${venueLabel(pool.venue)} · ${label}` : venueLabel(pool.venue)
}

/** "12 minutes ago", for a Chainlink round. */
export function formatAge(seconds: number | null): string {
  if (seconds === null) return DASH
  if (seconds < 90) return `${seconds}s ago`
  const minutes = Math.round(seconds / 60)
  if (minutes < 90) return `${minutes} min ago`
  const hours = Math.round(minutes / 60)
  if (hours < 36) return `${hours} h ago`
  return `${Math.round(hours / 24)} d ago`
}

export function basescanToken(address: string): string {
  return `https://basescan.org/token/${address}`
}

export function basescanAddress(address: string): string {
  return `https://basescan.org/address/${address}`
}

/** The swap, with both sides already chosen. USDC in, the stock out. */
export function swapHref(stockAddress: string, quoteAddress: string): string {
  return `/swap?tab=swap&in=${quoteAddress}&out=${stockAddress}`
}
